import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable,
    inject
} from "@angular/core";

import {
    IInternalApiClient,
    InternalApiRequestService
} from "@shopliftr/common-ng";
import {
    ReportInstance,
    ReportWindowSubmission,
    RequestUtil
} from "@shopliftr/common-js/shared";

/**
 * API Client Service for ReportInstance operations.
 *
 * Provides a focused interface for fetching report instances from the Internal API.
 * This service is the API communication layer that wraps the `report/instance` submit
 * endpoint, and is intended to be consumed by `ReportInstanceService` (which implements
 * `IListDataService`) rather than called directly from components.
 *
 * **Service Pattern:** API Client Service — submit-only, no CRUD interface required
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @implements {IInternalApiClient} Base internal API client pattern (basePath only)
 *
 * @example
 * ```typescript
 * // In a consuming service or component, declare the dependency and use it:
 * getReportData(window: ReportWindowSubmission): Observable<ReportInstance> {
 *     return this._reportInstanceApiClientService.getReportInstance$(window);
 * }
 * ```
 *
 * @providedIn root
 */
@Injectable({ providedIn: "root" })
export class ReportInstanceApiClientService implements IInternalApiClient {

    /**
     * Base API path for report instance endpoints.
     */
    readonly basePath = "report/instance";

    /**
     * Handles HTTP communication with Internal API for report instance operations.
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves a report instance for the specified report window.
     *
     * @param reportWindow - Report window configuration for data retrieval
     * @returns Observable that emits the report instance with data
     */
    getReportInstance$(reportWindow: ReportWindowSubmission): Observable<ReportInstance> {
        const submitRequest = RequestUtil.createSubmitRequest(reportWindow);
        return this._internalApiRequestService.submit$<ReportWindowSubmission, ReportInstance>(
            submitRequest, this.basePath
        ).pipe(map((response) => response.payload));
    }
}
